import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from 'rebass';
import Image from 'gatsby-image';
import ParallaxJS from 'parallax-js';
import throttle from 'lodash.throttle';
import posed from 'react-pose';
import { fromPairs, pathOr, toPairs } from 'ramda';

const Parallax = ({ layers = [], animation = null, disabled = false }) => {
	const sceneRef = useRef(null);
	const parallaxRef = useRef(null);
	const [sceneRect, setRect] = useState({ width: 1920, height: 1080 });

	useEffect(() => {
		setRect(sceneRef.current.getBoundingClientRect());
		const resizeRect = throttle(() => {
			if (sceneRef.current) { setRect(sceneRef.current.getBoundingClientRect()); };
		}, 500);

		window.addEventListener('resize', resizeRect);
		return () => {
			window.removeEventListener('resize', resizeRect)
		}
	}, []);

	useEffect(() => {
		const parallax = new ParallaxJS(sceneRef.current, {
			relativeInput: true,
			clipRelativeInput: true
		});
		parallaxRef.current = parallax;

		return () => {
			parallax.destroy();
		}
	}, [layers]);

	const mappedLayers = useMemo(() =>
		layers.map(({ animation, ...layer }) => ({
			...layer,
			animation,
			Component:
				animation
					? posed(Box)({
						animated: {
							...fromPairs(toPairs(animation.pose).filter(([_, value]) => Boolean(value))),
							transition: ({ from, to }) => {
								return ({
									type: 'keyframes',
									// CSS variables sometimes pick up whitespace, so we need to trim them if using in an animation:
									...animation.keyframes,
									values: animation.keyframes.values.map(v => {
										if (!Number.isNaN(Number(v))) {
											return Number(v)
										}
										const res = ({ to, from })[v];
										return typeof res !== "undefined" ? res : v;
									}),
									loop: !disabled ? Infinity : null,
								})
							}
						} })
					: Box,
		})), [layers, disabled]);

	useEffect(() => {
		if (parallaxRef.current && (parallaxRef.current.enabled === disabled)) {
			disabled ? parallaxRef.current.disable() : parallaxRef.current.enable();
		}
	}, [disabled]);

	const sceneRatio = sceneRect.width /  sceneRect.height;

	return (
		<Box
			sx={{
				height: '100%',
				width: '100%',
				left: 0,
				top: 0,
		}}>
			<Box
				sx={{
					position: 'absolute',
					height: '105%',
					width: '105%',
					left: '-2.5%',
					top: '-2.5%',
					margin: 0,
					padding: 0,
					listStyle: 'none'
				}}
				as="ul"
				ref={sceneRef}
			>
				{mappedLayers.map(({ depth, overflow, image, offsetY = 0, animation, Component, ...rest }) => {
					const { childImageSharp: { fluid: { aspectRatio } } } = image;
					const styles = pathOr({}, ['styles'], animation);
					const sx = pathOr({} , ['sx'], rest);

					let position = {};
					if (overflow) {
						if (aspectRatio >= sceneRatio) {
							const height = sceneRect.height;
							const width = height * aspectRatio;
							position = {
								height,
								width,
								transform: `translate(${-(width - sceneRect.width) / 2 + (width * offsetY / 100)}px, 0px)`,
							};
						} else {
							const width = sceneRect.width;
							const height = width / aspectRatio;
							position = {
								height,
								width,
								transform: `translate(${(width * offsetY / 100)}px, ${-(height - sceneRect.height) / 2}px)`,
							};
						}
					}
					return (
						<Box
							as="li"
							className="layer"
							data-depth={depth}
							key={image.id}
							m={0} p={0}
							sx={{
								listStyle: 'none',
								width: '100%',
								height: '100%',
							}}
						>
							<Box
								sx={{
									width: '100%',
									height: '100%',
									...position,
								}}
							>
								<Component
									initialPose="none"
									pose={"animated"}
									disabled={disabled}
									sx={{
										...styles,
										...sx,
										width: '100%',
										height: '100%',
										'> div': {
											height: '100%',
										}
									}}
								>
								{<Image height={'100%'} fluid={image.childImageSharp.fluid}/>}
								</Component>
							</Box>
						</Box>
					)
				})}
			</Box>
		</Box>
	);
};

export default Parallax;